import * as React from 'react'
import { useRouter } from 'next/compat/router'

import {
  QubitRecommendations,
  RecommendationSourceType,
} from '@thg-commerce/enterprise-components/Qubit'
import { parseQubitPreviewParams } from '@thg-commerce/enterprise-components/Qubit/utils'
import {
  i18n,
  useSessionSettings,
  useSiteConfig,
  useSiteDefinition,
} from '@thg-commerce/enterprise-core'
import { withESI } from '@thg-commerce/enterprise-esi'
import { RecommendationTrackingContext } from '@thg-commerce/enterprise-metrics/src/backend_event/types/tracking'
import { ProductPageData } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Product/ProductPage'
import {
  InStockLocation,
  ProductRecommendationType,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import {
  findOption,
  getColour,
  getColourSwatches,
} from '@thg-commerce/enterprise-network/src/transformers/product/colorSwatch'
import { getRrp } from '@thg-commerce/enterprise-pricing/src'
import { useTheme } from '@thg-commerce/enterprise-theme'
import { ProductRecommendations } from '@thg-commerce/enterprise-widget-product-recommendations/src/ProductRecommendations'
import { ProductOptionKeys } from '@thg-commerce/enterprise-widgets/src/ProductsShowcaseRenderer/ProductsShowcaseRenderer'
import { ColourChoice } from '@thg-commerce/gravity-elements/Swatch'
import { spacing, styled, Text } from '@thg-commerce/gravity-theme'
import { TextStyling } from '@thg-commerce/gravity-theme/typography/typography'

import { OtherCustomersBought } from '../../components'

export const Title = styled.h2<{ textStyle?: TextStyling; textAlign?: string }>`
  ${(props) =>
    Text(
      props.textStyle?.entry || 'xlarge1',
      props.textStyle?.style || 'alternate',
    )};
  text-transform: ${(props) => props.textStyle?.transform || 'none'};
  text-decoration: ${(props) => props.textStyle?.textDecoration || 'none'};
  color: ${(props) => props.textStyle?.textColor || '#333333'};
  text-align: ${(props) => props.textAlign || 'center'};
  padding: ${spacing(4)} 0 ${spacing(3)};
`

const QubitRecommendationsESI = withESI(
  QubitRecommendations,
  'qubit-recommendations',
)

const getProductBlocks = (displayRecommendationsBuild, product) => {
  const rrpText = i18n('product.price.rrp.text')
  const fromText = i18n('product.price.from.text')

  const getPriceValue = (price) => price || undefined
  const productBlocks = (
    (displayRecommendationsBuild
      ? product?.manualRecommendations
      : product?.recommendations) || []
  ).map((recommendedProduct) => {
    const recommendedProductPrice =
      recommendedProduct.defaultVariant?.price ||
      recommendedProduct.cheapestVariant?.price

    return {
      sku: recommendedProduct.sku,
      colourSwatches: recommendedProduct.colourSwatches,
      externalIdentifier: recommendedProduct.externalIdentifier,
      marketedSpecialOffer: recommendedProduct.marketedSpecialOffer,
      title: recommendedProduct.title,
      brand: {
        name: recommendedProduct.brand?.name,
        imageUrl: product.brand?.imageUrl,
      },
      url: recommendedProduct.url,
      image: recommendedProduct.images[0]?.largeProduct || '',
      reviews: {
        averageScore: recommendedProduct.reviews?.averageScore ?? 0,
        total: recommendedProduct.reviews?.total ?? 0,
      },
      price: {
        rrpText,
        fromText,
        price: {
          defaultPrice: recommendedProductPrice?.price.displayValue || '',
          minPrice: getPriceValue(
            recommendedProduct.cheapestVariant?.price?.price.displayValue,
          ),
          maxPrice: getPriceValue(
            recommendedProduct.mostExpensiveVariant?.price?.price.displayValue,
          ),
        },
        rrp:
          (recommendedProductPrice && {
            defaultPrice: getRrp(recommendedProductPrice),
            minPrice:
              recommendedProduct.cheapestVariant?.price &&
              getRrp(recommendedProduct.cheapestVariant.price),
            maxPrice:
              recommendedProduct.mostExpensiveVariant?.price &&
              getRrp(recommendedProduct.mostExpensiveVariant.price),
          }) ||
          undefined,
      },
      priceRange: recommendedProduct.priceRange,
      rrpRange: recommendedProduct.rrpRange,
      inStock: recommendedProduct.inStock,
      defaultVariant: getPriceValue(recommendedProduct.defaultVariant),
      categories: recommendedProduct.categories,
      content: recommendedProduct.content,
    }
  })
  return productBlocks
}

export const ProductRecommendationsRenderer = ({
  product,
  trackingContext,
  displayAsGrid,
  displayRecommendationsBuild,
}: {
  product?: ProductPageData
  trackingContext?: RecommendationTrackingContext
  displayAsGrid?: boolean
  displayRecommendationsBuild?: boolean
}) => {
  const i18nText = {
    productRecommendationsTitle: i18n('product.recommendations.title.text'),
    recommendationsBuildTitle: i18n('product.recommendations.build.title.text'),
    swatchTooltipUnavailable: i18n(
      'product.productoptions.swatch.tooltip.unavailable.text',
    ),
    swatchTooltipCloseButton: i18n(
      'product.productoptions.swatch.tooltip.close.text',
    ),
    fromText: i18n('product.price.from.text'),
  }

  const {
    qubit,
    showPdpLinkWhenOutOfStock,
    disableSavingOnRecommendations,
    enableAlternateProductRecommendations,
    enableQuickBuyOnPDPRecs,
  } = useSiteConfig()
  const { domain } = useSiteDefinition()
  const sessionSettings = useSessionSettings()
  const router = useRouter()
  const theme = useTheme()
  const qubitPreviewParams = parseQubitPreviewParams(
    `https://${domain}${router.asPath}`,
  )

  if (!product) {
    return null
  }

  if (
    qubit?.enabled &&
    typeof qubit.productPageRecommendationsPlacementId === 'string'
  ) {
    return (
      <QubitRecommendationsESI
        placementId={
          qubitPreviewParams?.placementId ??
          qubit.productPageRecommendationsPlacementId
        }
        mode={qubitPreviewParams?.mode ?? qubit.mode}
        previewOptions={{
          campaignId: qubitPreviewParams.campaignId,
          experienceId: qubitPreviewParams.experienceId,
          group: qubitPreviewParams.group,
        }}
        title={i18nText.productRecommendationsTitle}
        product={{
          sku: product.sku,
          title: product.title,
        }}
        source={{
          type: RecommendationSourceType.PRODUCT_PAGE,
          sku: product.sku,
          currency: sessionSettings.currency,
          shippingDestination: sessionSettings.shippingDestination,
          recommendationsType:
            ProductRecommendationType.FrequentlyBoughtTogether,
        }}
      />
    )
  }

  // TODO: CSBOM-405 to revisit the swatches logic implementation
  const productBlocks = getProductBlocks(displayRecommendationsBuild, product)

  return productBlocks.length ? (
    <React.Fragment>
      <Title
        textStyle={theme.widget?.productRecommendations?.title?.font}
        textAlign={theme.widget?.productRecommendations?.title?.align}
      >
        {displayRecommendationsBuild
          ? i18nText.recommendationsBuildTitle
          : i18nText.productRecommendationsTitle}
      </Title>
      <ProductRecommendations
        products={productBlocks}
        displayAsGrid={displayAsGrid}
        displayAlternateStyle={enableAlternateProductRecommendations}
        enableSavePrice={disableSavingOnRecommendations}
        itemsPerSlide={
          theme.widget?.productRecommendations?.carousel?.itemsPerSlide || [
            1,
            2,
            3,
            4,
          ]
        }
        trackingContext={trackingContext}
        recommendationsStyle={theme.widget?.productRecommendations}
        showPdpLinkWhenOutOfStock={showPdpLinkWhenOutOfStock}
        widgetTitle={i18nText.productRecommendationsTitle}
        carouselSize={theme.widget?.productRecommendations?.carousel?.size}
        enableQuickBuyOnPDPRecs={enableQuickBuyOnPDPRecs}
      />
    </React.Fragment>
  ) : null
}
